import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { DISPLAY_NAME_LENGTH, type ImportAvailability } from '@cribbage/protocol';
import { api } from '~/api/client.js';
import { syncPendingGames } from '~/session/offlineSync.js';
import { useAuthStore } from '~/store/authStore.js';
import { toFriendlyError } from '~/store/sessionStore.js';
import { useLatestRequest } from '~/ui/useLatestRequest.js';
import { emailProblem, newPasswordProblem } from './accountChecks.js';

/**
 * Creating an account or signing in, whichever screen asks.
 *
 * Two screens ask: `/auth`, which the online doors bounce through, and the
 * Account group in Settings, which asks inside its own frame. They draw their
 * forms differently and must not behave differently, so the behavior is here
 * and each screen draws what it needs.
 *
 * Signing in is the only path that can leave games behind. Registering adopts
 * the device's own player, so everything played before the account existed is
 * already the account's. Signing in switches to a player who existed already,
 * and the games on this device stay with whoever played them until they say
 * otherwise: that is the one-time offer in `ImportPrompt`, whose state lives
 * here too.
 */

const MIN_NAME = DISPLAY_NAME_LENGTH.min;
const MAX_NAME = DISPLAY_NAME_LENGTH.max;

/**
 * How long a sign-in waits for the device's games before going in without them.
 *
 * Long enough for a real upload on a slow connection, short enough that nobody
 * is left staring at a form they already filled in correctly.
 */
const DRAIN_TIMEOUT_MS = 5000;

/**
 * How long a sign-in waits to hear whether there is anything to import.
 *
 * The question holds the form's submit and its mode swap until it is answered,
 * so a server that never answers cannot be allowed to hold them. A question
 * that runs out of time is a question that failed: the sign-in goes in without
 * the offer, and the next sign-in asks again.
 */
const IMPORT_QUESTION_TIMEOUT_MS = 5000;

/**
 * Get this device's games up, and say whether the server has all of them.
 *
 * Bounded, because `api/client.ts` sends no `AbortSignal`: an upload that stalls
 * never settles, and awaiting it left `signingIn` set and the redirect blocked
 * for a sign-in that had already worked. A drain that times out is treated
 * exactly like one that failed — the games are still on the device, and the
 * offer can be made the next time somebody signs in.
 *
 * A deferred game counts as not up. The server took the batch and could not
 * decide, so those games land *after* this sign-in — and if the offer is
 * accepted in the meantime the import retires this device's local player, so
 * the late arrivals land on a player nobody will be offered again. False here
 * is the cheap side: the games stay on the device and the next sign-in asks.
 *
 * `held` does not gate it. A `wrong_player` record belongs to a player who may
 * never sign in on this device again, so waiting for it to clear would suppress
 * the offer forever rather than for one attempt.
 */
async function drainPendingGames(): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const expiry = new Promise<null>((resolve) => {
    timer = setTimeout(() => {
      resolve(null);
    }, DRAIN_TIMEOUT_MS);
  });
  try {
    const outcome = await Promise.race([syncPendingGames().catch(() => null), expiry]);
    return outcome !== null && !outcome.failed && outcome.deferred === 0;
  } finally {
    clearTimeout(timer);
  }
}

export type AuthField = 'email' | 'password' | 'displayName';

export interface AuthInput {
  readonly email: string;
  readonly password: string;
  /** Read only when registering. */
  readonly displayName: string;
}

export interface AuthEntry {
  /** What the form refused before anything was sent, one line per field. */
  readonly problems: Partial<Record<AuthField, string>>;
  /**
   * The server's refusal as one line, or null when there is none. The store
   * files a 401 as "sign in again", which is for a stale session; a refused
   * sign-in reads as one sentence naming neither field, so it cannot confirm
   * which email has an account. Any other refusal keeps the store's words.
   */
  readonly refusal: string | null;
  /**
   * True from a sign-in's first request until it has gone wherever it was
   * going, so the screen's own "already has an account" redirect cannot fire
   * in the middle of the import offer.
   */
  readonly signingIn: boolean;
  /** The one-time import offer, while it is on screen. */
  readonly importing: ImportAvailability | null;
  readonly importBusy: boolean;
  readonly importError: string | null;
  readonly register: (input: AuthInput) => Promise<void>;
  readonly signIn: (input: AuthInput) => Promise<void>;
  readonly answerImport: (accept: boolean) => Promise<void>;
  /** Forget the refusals, the form's and the server's, when the form changes. */
  readonly reset: () => void;
}

/**
 * `onDone` is where a finished sign-in or registration goes. It is called
 * once, after the import offer has been answered when there was one.
 */
export function useAuthEntry(onDone: () => void): AuthEntry {
  const { t } = useTranslation();
  const [problems, setProblems] = useState<Partial<Record<AuthField, string>>>({});
  const [signingIn, setSigningIn] = useState(false);
  const [importing, setImporting] = useState<ImportAvailability | null>(null);
  const [importBusy, setImportBusy] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  /*
   * Which submit the store's error answers. Each screen clears the error when
   * its form changes, so whatever is left is the answer to this attempt.
   */
  const [attempt, setAttempt] = useState<'register' | 'signIn' | null>(null);
  const serverError = useAuthStore((state) => state.error);
  /*
   * Each submit and each answer to the offer owns what follows its waits. The
   * Settings pages can be left while a sign-in is in the air, and leaving
   * retires the request, so a late answer neither sets state on a page that is
   * gone nor sends the player somewhere they have already left.
   */
  const request = useLatestRequest();

  const check = (input: AuthInput, registering: boolean): boolean => {
    const found = validate({ ...input, registering }, t);
    setProblems(found);
    return Object.keys(found).length === 0;
  };

  const register = async (input: AuthInput): Promise<void> => {
    if (!check(input, true)) return;
    const isCurrent = request.begin();
    setAttempt('register');
    // Registering replaces a sign-in still in the air, and that sign-in's
    // flag has to go with it or the screen's redirect waits on it forever.
    setSigningIn(false);
    // The new account adopts this device's player, so its games are already
    // the account's and there is nothing left on the device to offer.
    const ok = await useAuthStore
      .getState()
      .register(input.email, input.password, input.displayName);
    if (ok && isCurrent()) onDone();
  };

  const signIn = async (input: AuthInput): Promise<void> => {
    if (!check(input, false)) return;
    const isCurrent = request.begin();
    setAttempt('signIn');
    setSigningIn(true);
    const ok = await useAuthStore.getState().login(input.email, input.password);
    if (!isCurrent()) return;
    if (!ok) {
      setSigningIn(false);
      return;
    }
    /*
     * The games played on this device before the sign-in have to be on the
     * server before we ask whether there is anything to import — the question
     * is asked once, and a "no" is remembered. The token swap fires a sync of
     * its own, but that one is not awaited by anybody, so this is the wait.
     * `syncPendingGames` is guarded against overlapping runs and never throws.
     *
     * A drain that failed, timed out, or left games deferred means the server
     * does not have all of them yet, and asking now would be asking about a
     * device that looks emptier than it is. The offer waits for a sign-in that
     * can answer it honestly.
     */
    if (await drainPendingGames()) {
      try {
        const available = await api.importAvailability(
          AbortSignal.timeout(IMPORT_QUESTION_TIMEOUT_MS),
        );
        if (!isCurrent()) return;
        if (available.available) {
          setImporting(available);
          return;
        }
      } catch {
        // The offer is a courtesy. A sign-in that worked is not held up because
        // the server could not answer a question about games nobody has lost,
        // or did not answer it in time.
      }
    }
    if (!isCurrent()) return;
    setSigningIn(false);
    onDone();
  };

  /** Answer the offer once, then go wherever the sign-in was heading. */
  const answerImport = async (accept: boolean): Promise<void> => {
    const isCurrent = request.begin();
    setImportBusy(true);
    setImportError(null);
    try {
      if (accept) {
        await api.acceptImport();
        // The account has more games and possibly a rating now, and the header
        // on the next screen reads from the store.
        await useAuthStore.getState().refresh();
      } else {
        await api.declineImport();
      }
    } catch (failure) {
      if (!isCurrent()) return;
      /*
       * A failed import is worth staying for: the games have not moved and the
       * question is only asked once. A failed decline is not, because the games
       * stay put either way and that is what declining asked for.
       */
      if (accept) {
        setImportBusy(false);
        setImportError(t(toFriendlyError(failure).key));
        return;
      }
    }
    if (!isCurrent()) return;
    setImportBusy(false);
    setImporting(null);
    setSigningIn(false);
    onDone();
  };

  const reset = (): void => {
    useAuthStore.getState().clearError();
    setProblems({});
    // A 401 that lands after this answers no sign-in, so it keeps the store's words.
    setAttempt(null);
  };

  const refusal =
    serverError === null
      ? null
      : attempt === 'signIn' && serverError.key === 'errors.unauthorized'
        ? t('auth.signInRefused')
        : t(serverError.key);

  return {
    problems,
    refusal,
    signingIn,
    importing,
    importBusy,
    importError,
    register,
    signIn,
    answerImport,
    reset,
  };
}

/** The shared account checks (`accountChecks.ts`), plus the name a registration picks. */
function validate(
  input: AuthInput & { registering: boolean },
  t: TFunction,
): Partial<Record<AuthField, string>> {
  const problems: Partial<Record<AuthField, string>> = {};

  const email = emailProblem(input.email, t);
  if (email !== undefined) problems.email = email;

  if (input.password === '') problems.password = t('auth.passwordRequired');
  else if (input.registering) {
    const short = newPasswordProblem(input.password, t);
    if (short !== undefined) problems.password = short;
  }

  if (input.registering) {
    const name = input.displayName.trim();
    if (name === '') problems.displayName = t('auth.displayNameRequired');
    else if (name.length < MIN_NAME || name.length > MAX_NAME) {
      problems.displayName = t('auth.displayNameLength', { min: MIN_NAME, max: MAX_NAME });
    }
  }

  return problems;
}
